import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, RefreshCw } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Hero } from '@/components/common/Hero';
import { PageLoader } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/common/EmptyState';
import { useCaseStudies } from '@/hooks/useCaseStudies';
import { useTranslation } from '@/features/i18n';

export function CaseStudiesPage() {
  const { data: caseStudies, isLoading, isError, refetch } = useCaseStudies();
  const { t } = useTranslation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError) {
    return (
      <>
        <SEO title={t.caseStudies.title} />
        <Hero
          title={t.caseStudies.title}
          subtitle={t.caseStudies.subtitle}
          description={t.caseStudies.description}
          size="md"
          variant="light"
        />
        <section className="ninito-section bg-[#edf1f5]">
          <div className="ninito-container">
            <EmptyState
              title={t.common?.error || 'Error loading data'}
              description={t.common?.tryAgain || 'Please try again later'}
              action={
                <button
                  onClick={() => refetch()}
                  className="ninito-btn ninito-btn-primary mt-4"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t.common?.retry || 'Retry'}
                </button>
              }
            />
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title={t.caseStudies.title}
        description={t.caseStudies.seoDescription}
      />

      <Hero
        title={t.caseStudies.title}
        subtitle={t.caseStudies.subtitle}
        description={t.caseStudies.description}
        size="md"
        variant="light"
      />

      <section className="ninito-section bg-[#edf1f5]">
        <div className="ninito-container">
          {caseStudies && caseStudies.length > 0 ? (
            <div className="grid lg:grid-cols-2 gap-7">
              {caseStudies.map((caseStudy, index) => (
                <motion.article
                  key={caseStudy.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/case-studies/${caseStudy.slug}`} className="block h-full group">
                    <div className="ninito-card h-full overflow-hidden p-0 ninito-hover-lift">
                      {caseStudy.cover_image_url && (
                        <div className="relative aspect-[16/9] overflow-hidden rounded-t-[28px]">
                          <img
                            src={caseStudy.cover_image_url}
                            alt={caseStudy.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {caseStudy.is_featured && (
                            <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-[#0078ff] text-white text-xs font-medium">
                              {t.caseStudies.featured}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="p-6 lg:p-8">
                        <div className="flex items-center gap-3 mb-4">
                          {caseStudy.industry && (
                            <span className="px-3 py-1 rounded-full bg-black/5 text-black/60 text-xs font-medium">
                              {caseStudy.industry}
                            </span>
                          )}
                        </div>
                        <h2 className="text-2xl font-semibold text-black tracking-tight group-hover:text-[#0078ff] transition-colors">
                          {caseStudy.title}
                        </h2>
                        <p className="mt-2 text-[#0078ff] font-medium">
                          {caseStudy.client_name}
                        </p>
                        <p className="mt-4 text-[#666] leading-relaxed line-clamp-3">
                          {caseStudy.problem_description}
                        </p>

                        {/* Results preview - ninito style */}
                        {caseStudy.results_json && caseStudy.results_json.length > 0 && (
                          <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-black/10">
                            {caseStudy.results_json.slice(0, 2).map((result, i) => (
                              <div key={i} className="text-center p-4 bg-[#edf1f5] rounded-2xl">
                                <div className="text-2xl font-bold text-[#0078ff]">
                                  {result.metric}
                                </div>
                                <div className="text-xs text-[#666] mt-1">
                                  {result.label}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-6 pt-6 border-t border-black/10">
                          <span className="text-[#0078ff] font-medium text-sm">
                            {t.caseStudies.readMore}
                          </span>
                          <ArrowUpRight className="w-5 h-5 text-[#0078ff] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          ) : (
            <EmptyState
              title={t.caseStudies.noCases}
              description={t.caseStudies.noCasesDesc}
            />
          )}
        </div>
      </section>

      {/* CTA - ninito dark style */}
      <section className="ninito-cta">
        <div className="relative ninito-container">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[32px] md:text-[40px] lg:text-[48px] font-semibold text-white tracking-[-0.02em]"
          >
            {t.caseStudies.cta.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-5 text-lg md:text-xl text-[#999] max-w-[600px] mx-auto"
          >
            {t.caseStudies.cta.description}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-10"
          >
            <Link to="/contact" className="ninito-btn ninito-btn-white ninito-btn-lg group">
              {t.caseStudies.cta.button}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
